import {
  Box,
  Flex,
  FormControl,
  HStack,
  Spinner,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {ChatIcon} from '@chakra-ui/icons';
import {useTranslation} from 'react-i18next';
import {useCommercial, useDevisVisible} from '@/haligator-factory';
import {useCommand, useHalState, useHalStates} from '@synako/haligator';
import {DevisSection} from './DevisSection';
import {dateAvecSlashes, formatDateForHistorique} from 'ff-common';
import {getMessageContent} from 'ff-common';
import {BlueButton} from '@/components/Button/BlueButton';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNouveauxMessagesDevis} from 'ff-common';

/**
 * Composant principal pour afficher le devis personnalisé côté client
 * Affiche les produits du devis groupés par catégorie
 */
export const DevisPersonnaliseContenu: React.FC = () => {
  const {t} = useTranslation();
  const {devis, loading, error} = useDevisVisible();
  const toast = useToast();

  // Lignes de produits du devis
  const lignesDeDevis = useHalStates(devis?.followAll('lignes_de_devis'));
  const nbreDeLignesDevis = lignesDeDevis.states?.length ?? 0;

  // State pour le commentaire du client
  const [commentaire, setCommentaire] = useState('');

  // Récupération du commercial
  const commercialState = useHalState(useCommercial());
  const commercial = commercialState.data;

  // Récupération de l'historique pour les messages
  const historiqueNav = devis?.follow('historique');
  const historiqueState = useHalState(historiqueNav);

  // Rafraîchissement automatique de l'historique toutes les 15 secondes
  useEffect(() => {
    const interval = window.setInterval(() => historiqueNav?.refresh(), 15_000);
    return () => {
      window.clearInterval(interval);
    };
  }, [historiqueNav]);

  // Commande pour marquer l'historique comme lu
  const marquerHistoriqueLu = useCommand(
    devis,
    'c:marquer-historique-devis-lu',
  );

  // Commande pour enregistrer la consultation du devis
  const consulterDevis = useCommand(devis, 'c:consulter-devis');

  // Enregistrer la consultation au chargement du devis
  useEffect(() => {
    if (devis && consulterDevis.isAvailable) {
      consulterDevis.submit({} as never);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devis?.data.id, consulterDevis.isAvailable]); // Déclencher uniquement quand le devis_id change

  // Compter les nouveaux messages et total des messages (calculé côté backend)
  const {nombreNouveauxMessages, nombreTotalMessages} =
    useNouveauxMessagesDevis(historiqueState);

  // State pour suivre si les messages ont été vus
  const [messagesVus, setMessagesVus] = useState(false);

  // Réinitialiser messagesVus quand de nouveaux messages arrivent
  useEffect(() => {
    if (nombreNouveauxMessages > 0) {
      setMessagesVus(false);
    }
  }, [nombreNouveauxMessages]);

  // Ref pour observer l'intersection avec la section des messages
  const messagesSectionRef = useRef<HTMLDivElement>(null);

  // Fonction pour marquer les messages comme lus
  const handleMarquerLu = useCallback(() => {
    if (!messagesVus && nombreNouveauxMessages > 0) {
      try {
        marquerHistoriqueLu.submit({});
        setMessagesVus(true);
      } catch (error) {
        console.error('Erreur lors du marquage des messages comme lus:', error);
      }
    }
  }, [messagesVus, nombreNouveauxMessages, marquerHistoriqueLu]);

  // State pour suivre si l'utilisateur a interagi (clic ou scroll intentionnel)
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // Écouter les interactions de scroll pour détecter si l'utilisateur fait défiler manuellement
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      // Déclencher l'interaction après un délai pour éviter les scrolls accidentels au chargement
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setUserHasInteracted(true);
      }, 1000); // 1 seconde de délai
    };

    window.addEventListener('scroll', handleScroll, {passive: true});

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Observer pour détecter quand la section des messages est visible SEULEMENT après interaction
  useEffect(() => {
    if (
      !messagesSectionRef.current ||
      messagesVus ||
      nombreNouveauxMessages === 0 ||
      !userHasInteracted
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && nombreNouveauxMessages > 0) {
            handleMarquerLu();
          }
        });
      },
      {
        threshold: [0.5], // Seuil plus élevé pour éviter les déclenchements accidentels
        rootMargin: '0px',
      },
    );

    observer.observe(messagesSectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [messagesVus, nombreNouveauxMessages, handleMarquerLu, userHasInteracted]);

  // Fonction pour faire défiler vers la section des messages et marquer comme lu
  const handleClickMessages = useCallback(() => {
    // Indiquer que l'utilisateur a interagi
    setUserHasInteracted(true);

    // Marquer comme lu au clic
    handleMarquerLu();

    // Faire défiler vers la section des messages avec un offset de 50px au-dessus
    const messagesSection = document.getElementById('messages-section');
    if (messagesSection) {
      const elementPosition = messagesSection.offsetTop;
      const offsetPosition = elementPosition - 50;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, [handleMarquerLu]);

  // Filtrer et trier tous les messages (commercial et client) par ordre chronologique
  const messages =
    historiqueState.data?.historique
      ?.filter((event) => {
        if (
          event.type !== 'message-devis.envoye' &&
          event.type !== 'commentaire-devis.envoye'
        ) {
          return false;
        }

        return getMessageContent(event.type, event.eventData ?? {});
      })
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA; // Inversé pour avoir les plus récents en premier
      }) ?? [];

  // Récupérer le dernier message du commercial (type 'message-devis.envoye')
  const dernierMessageCommercial = messages.find(
    (message) => message.type === 'message-devis.envoye',
  );

  // Commande pour envoyer le commentaire du client au commercial
  const envoyerCommentaire = useCommand(devis, 'c:envoyer-commentaire-client', {
    onSuccess: () => {
      toast({
        title: t('liste.devis_personnalise.commentaire_envoye_succes'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setCommentaire('');
    },
    onError: (error) => {
      toast({
        title: t('global.erreur'),
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleEnvoyerCommentaire = () => {
    if (!commentaire.trim()) {
      toast({
        title: t('liste.devis_personnalise.commentaire_requis'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    envoyerCommentaire.submit({
      contenu: commentaire.trim(),
    });
  };

  // Chargement
  if (loading) {
    return (
      <Box
        textAlign='center'
        py={8}
      >
        <Spinner size='lg' />
        <Text mt={4}>{t('global.chargement')}</Text>
      </Box>
    );
  }

  // Erreur
  if (error) {
    return (
      <Box
        textAlign='center'
        py={8}
      >
        <Text color='red.500'>{t('global.erreur_chargement')}</Text>
      </Box>
    );
  }

  // Aucun devis visible
  if (!devis) {
    return (
      <Box
        textAlign='center'
        py={8}
      >
        <Text color='gray.500'>
          {t('liste.devis_personnalise.aucun_devis')}
        </Text>
      </Box>
    );
  }

  const derniereMaj = devis.data.modifie_le ?? devis.data.cree_le;

  return (
    <VStack
      align='stretch'
      spacing={2}
      width='100%'
    >
      {/* En-tête avec informations du devis */}
      <Box
        bg='white'
        p={4}
      >
        <Flex
          flexDirection={{base: 'column', md: 'row'}}
          justifyContent={'space-between'}
          bg='blue.sky_light'
          borderRadius='8px'
          p='10px'
        >
          <VStack
            alignItems='start'
            justifyContent={'center'}
            spacing={1}
            w='100%'
          >
            <Text
              fontSize='md'
              fontWeight='bold'
              color='dark.text'
            >
              {commercial
                ? t('liste.devis_personnalise.selection_commercial_avec_nom', {
                    prenom: commercial.tolede.individu.prenom,
                    nom: commercial.tolede.individu.nom,
                  })
                : t('liste.devis_personnalise.selection_commercial')}
            </Text>
            <HStack>
              <Text
                fontSize='sm'
                color='blue.main'
                p='5px'
              >
                {t('global.produit', {count: nbreDeLignesDevis})}
              </Text>
              <Text
                fontSize='sm'
                color='gray.dark'
                fontStyle='italic'
                p='5px'
              >
                {t('liste.devis_personnalise.mis_a_jour_le', {
                  date: dateAvecSlashes(derniereMaj),
                })}
              </Text>
            </HStack>
          </VStack>
          {/* Section des messages */}
          {nombreTotalMessages > 0 && (
            <VStack
              spacing={1}
              minW={{md: '240px'}}
              maxW={{md: '400px', lg: '700px'}}
              bg='white'
              p='10px'
              borderRadius='8px'
              justifyContent='center'
              alignItems='start'
              onClick={handleClickMessages}
            >
              <HStack
                spacing={2}
                h='30px'
                mt={0}
                borderRadius='8px'
                p='5px'
                justifyContent='center'
              >
                <Box position='relative'>
                  <ChatIcon
                    color='blue.main'
                    boxSize={5}
                  />
                  {/* Badge pour les nouveaux messages */}
                  {nombreNouveauxMessages > 0 && (
                    <Box
                      position='absolute'
                      top='1px'
                      right='-2px'
                      bg='red.500'
                      borderRadius='full'
                      w='10px'
                      h='10px'
                    />
                  )}
                </Box>
                <Text
                  fontSize='sm'
                  color='blue.main'
                  fontWeight='600'
                  cursor='pointer'
                  _hover={{textDecoration: 'underline'}}
                >
                  {t('accueil.devis_personnalises.messages', {
                    count: nombreTotalMessages,
                  })}
                </Text>
                <Text
                  fontSize='sm'
                  color='blue.main'
                >
                  (
                  {t('liste.devis_personnalise.non_lu', {
                    count: nombreNouveauxMessages,
                  })}
                  )
                </Text>
              </HStack>

              {/* Affichage du dernier message du commercial */}
              {nombreNouveauxMessages > 0 && dernierMessageCommercial && (
                <Box
                  flex='1'
                  width='100%'
                >
                  <Text
                    fontSize='sm'
                    color='gray.dark'
                    cursor='pointer'
                    wordBreak='break-word'
                  >
                    {getMessageContent(
                      dernierMessageCommercial.type ?? '',
                      dernierMessageCommercial.eventData ?? {},
                    )}
                  </Text>
                </Box>
              )}
            </VStack>
          )}
        </Flex>
      </Box>

      {/* Liste des produits du devis groupés par catégorie */}
      <Box bg='white'>
        <DevisSection
          lignesDeDevis={lignesDeDevis}
          tri_selection_devis={devis.data.tri_selection_devis}
        />
      </Box>

      {/* Section commentaires du client au commercial - formulaire d'envoi */}
      {devis.data.statut === 'visible' && (
        <Box
          bg='white'
          p={4}
          pt={0}
        >
          <Text
            fontSize='md'
            fontWeight='700'
            mb={4}
            color='dark.text'
          >
            {t('liste.devis_personnalise.commentaires')}
          </Text>
          <VStack
            align='stretch'
            spacing={0}
          >
            <FormControl>
              <Textarea
                value={commentaire}
                onChange={(e) => {
                  setCommentaire(e.target.value);
                }}
                placeholder={t('liste.devis_personnalise.ecrire_commentaire')}
                rows={4}
                resize='vertical'
                maxLength={2000}
              />
              <Text
                fontSize='xs'
                color='gray.dark'
                textAlign='right'
              >
                {commentaire.length}/2000
              </Text>
            </FormControl>
            <HStack justify='flex-start'>
              <BlueButton
                onClick={handleEnvoyerCommentaire}
                isLoading={envoyerCommentaire.isSubmitting}
                isDisabled={!commentaire.trim()}
                texte={t('global.envoyer')}
                mt={0}
              />
            </HStack>
          </VStack>
        </Box>
      )}

      {/* Messages du commercial et du client */}
      {messages.length > 0 && (
        <Box
          id='messages-section'
          ref={messagesSectionRef}
          bg='white'
          p={4}
        >
          <VStack
            align='stretch'
            spacing={3}
          >
            {messages.map((message, index) => {
              const isCommercial = message.type === 'message-devis.envoye';
              const bgColor = isCommercial ? 'blue.sky_light' : 'gray.bg';
              const labelDuMessage = isCommercial
                ? t('liste.devis_personnalise.votre_commercial')
                : t('liste.devis_personnalise.vous');

              return (
                <Box key={index}>
                  <HStack
                    justify='space-between'
                    mb={2}
                  >
                    <Text
                      fontSize='sm'
                      fontWeight='700'
                    >
                      {labelDuMessage}
                    </Text>
                    {message.date && (
                      <Text
                        fontSize='xs'
                        color='gray.dark'
                      >
                        {formatDateForHistorique(message.date)}
                      </Text>
                    )}
                  </HStack>
                  <Box
                    p={3}
                    bg={bgColor}
                    borderRadius='8px'
                  >
                    <Text
                      fontSize='sm'
                      color='black.text'
                      fontStyle='italic'
                      whiteSpace='pre-wrap'
                    >
                      &quot;
                      {getMessageContent(
                        message.type ?? '',
                        message.eventData ?? {},
                      )}
                      &quot;
                    </Text>
                  </Box>
                </Box>
              );
            })}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};
